import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Quote,
  QuotesState,
  AddQuotePayload,
  UpdateQuotePayload,
  SetDailyQuotePayload,
} from '../types/quote';
import { generateUUID } from '../utils/uuid';

/**
 * Initial state for quotes slice
 */
const initialState: QuotesState = {
  quotes: [],
  dailyQuoteId: null,
  lastSelectedDate: null,
};

/**
 * Quotes slice - manages all quote-related state
 */
const quotesSlice = createSlice({
  name: 'quotes',
  initialState,
  reducers: {
    /**
     * Add a new quote to the collection
     */
    addQuote: (state, action: PayloadAction<AddQuotePayload>) => {
      const now = Date.now();
      const newQuote: Quote = {
        id: generateUUID(),
        text: action.payload.text,
        author: action.payload.author,
        createdAt: now,
        updatedAt: now,
      };
      state.quotes.push(newQuote);
    },

    /**
     * Update an existing quote
     */
    updateQuote: (state, action: PayloadAction<UpdateQuotePayload>) => {
      const quote = state.quotes.find((q) => q.id === action.payload.id);
      if (quote) {
        quote.text = action.payload.text;
        quote.author = action.payload.author;
        quote.updatedAt = Date.now();
      }
    },

    /**
     * Delete a quote by ID
     * If the deleted quote was the daily quote, clear dailyQuoteId
     */
    deleteQuote: (state, action: PayloadAction<string>) => {
      const quoteId = action.payload;
      state.quotes = state.quotes.filter((q) => q.id !== quoteId);

      // If we deleted the currently displayed daily quote, clear it
      if (state.dailyQuoteId === quoteId) {
        state.dailyQuoteId = null;
        state.lastSelectedDate = null;
      }
    },

    /**
     * Set the daily quote for a specific date
     * Used by the daily quote selection logic
     */
    setDailyQuote: (state, action: PayloadAction<SetDailyQuotePayload>) => {
      state.dailyQuoteId = action.payload.quoteId;
      state.lastSelectedDate = action.payload.date;
    },

    /**
     * Clear the daily quote (used for manual refresh)
     * Forces reselection on next load
     */
    clearDailyQuote: (state) => {
      state.dailyQuoteId = null;
      state.lastSelectedDate = null;
    },

    /**
     * Bulk import quotes (for future backup/restore feature)
     */
    importQuotes: (state, action: PayloadAction<Quote[]>) => {
      state.quotes = action.payload;
      // Clear daily quote when importing to force reselection
      state.dailyQuoteId = null;
      state.lastSelectedDate = null;
    },

    /**
     * Clear all quotes (destructive - use with caution)
     */
    clearAllQuotes: (state) => {
      state.quotes = [];
      state.dailyQuoteId = null;
      state.lastSelectedDate = null;
    },
  },
});

// Export actions
export const {
  addQuote,
  updateQuote,
  deleteQuote,
  setDailyQuote,
  clearDailyQuote,
  importQuotes,
  clearAllQuotes,
} = quotesSlice.actions;

// Export reducer
export default quotesSlice.reducer;
